import { apiClient } from './client';
import { downloadPdfBlob } from '../utils/downloadPdf';
import {
  SafetyCheckSheet,
  SafetyCheckSheetAttachment,
  SafetyCheckSheetPayload,
  SafetyPpeIssuance,
  SafetyPpeIssuanceAttachment,
  SafetyPpeIssuancePayload,
} from '../types/safety';
import { SequentialCodeSuggestion } from '../types/sequentialCode';

export async function fetchSafetyPpeIssuances(
  keyword?: string,
  year?: number | null,
  contractId?: number | null
): Promise<SafetyPpeIssuance[]> {
  const params: Record<string, string | number> = {};
  if (keyword?.trim()) params.q = keyword.trim();
  if (year != null) params.year = year;
  if (contractId != null) params.contractId = contractId;
  const { data } = await apiClient.get<SafetyPpeIssuance[]>('/safety/ppe-issuances', {
    params: Object.keys(params).length ? params : undefined,
  });
  return data;
}

export async function fetchNextSafetyPpeDocNo(): Promise<SequentialCodeSuggestion> {
  const { data } = await apiClient.get<SequentialCodeSuggestion>(
    '/safety/ppe-issuances/suggestions/doc-no'
  );
  return data;
}

export async function fetchSafetyPpeIssuance(issuanceId: number): Promise<SafetyPpeIssuance> {
  const { data } = await apiClient.get<SafetyPpeIssuance>(`/safety/ppe-issuances/${issuanceId}`);
  return data;
}

export async function createSafetyPpeIssuance(
  payload: SafetyPpeIssuancePayload
): Promise<SafetyPpeIssuance> {
  const { data } = await apiClient.post<SafetyPpeIssuance>('/safety/ppe-issuances', payload);
  return data;
}

export async function updateSafetyPpeIssuance(
  issuanceId: number,
  payload: SafetyPpeIssuancePayload
): Promise<SafetyPpeIssuance> {
  const { data } = await apiClient.patch<SafetyPpeIssuance>(
    `/safety/ppe-issuances/${issuanceId}`,
    payload
  );
  return data;
}

export async function deleteSafetyPpeIssuance(issuanceId: number): Promise<void> {
  await apiClient.delete(`/safety/ppe-issuances/${issuanceId}`);
}

export async function approveSafetyPpeIssuance(issuanceId: number): Promise<SafetyPpeIssuance> {
  const { data } = await apiClient.post<SafetyPpeIssuance>(
    `/safety/ppe-issuances/${issuanceId}/approve`
  );
  return data;
}

export async function revokeSafetyPpeIssuance(issuanceId: number): Promise<SafetyPpeIssuance> {
  const { data } = await apiClient.post<SafetyPpeIssuance>(
    `/safety/ppe-issuances/${issuanceId}/revoke`
  );
  return data;
}

export async function uploadSafetyPpeIssuanceAttachments(
  issuanceId: number,
  files: File[]
): Promise<SafetyPpeIssuanceAttachment[]> {
  const form = new FormData();
  files.forEach((file) => form.append('files', file));
  const { data } = await apiClient.post<SafetyPpeIssuanceAttachment[]>(
    `/safety/ppe-issuances/${issuanceId}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function deleteSafetyPpeIssuanceAttachment(
  issuanceId: number,
  attachmentId: number
): Promise<void> {
  await apiClient.delete(`/safety/ppe-issuances/${issuanceId}/attachments/${attachmentId}`);
}

export async function downloadSafetyPpeIssuanceAttachment(
  issuanceId: number,
  attachmentId: number,
  fileName: string
): Promise<void> {
  const { data } = await apiClient.get<Blob>(
    `/safety/ppe-issuances/${issuanceId}/attachments/${attachmentId}/download`,
    { responseType: 'blob' }
  );
  const url = window.URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(url);
}

export async function fetchSafetyCheckSheets(
  keyword?: string,
  contractId?: number | null
): Promise<SafetyCheckSheet[]> {
  const params: Record<string, string | number> = {};
  if (keyword?.trim()) params.q = keyword.trim();
  if (contractId != null) params.contractId = contractId;
  const { data } = await apiClient.get<SafetyCheckSheet[]>('/safety/check-sheets', {
    params: Object.keys(params).length ? params : undefined,
  });
  return data;
}

export async function fetchNextSafetyCheckSheetNo(): Promise<SequentialCodeSuggestion> {
  const { data } = await apiClient.get<SequentialCodeSuggestion>(
    '/safety/check-sheets/suggestions/sheet-no'
  );
  return data;
}

export async function fetchSafetyCheckSheet(sheetId: number): Promise<SafetyCheckSheet> {
  const { data } = await apiClient.get<SafetyCheckSheet>(`/safety/check-sheets/${sheetId}`);
  return data;
}

export async function createSafetyCheckSheet(
  payload: SafetyCheckSheetPayload
): Promise<SafetyCheckSheet> {
  const { data } = await apiClient.post<SafetyCheckSheet>('/safety/check-sheets', payload);
  return data;
}

export async function updateSafetyCheckSheet(
  sheetId: number,
  payload: SafetyCheckSheetPayload
): Promise<SafetyCheckSheet> {
  const { data } = await apiClient.patch<SafetyCheckSheet>(
    `/safety/check-sheets/${sheetId}`,
    payload
  );
  return data;
}

export async function deleteSafetyCheckSheet(sheetId: number): Promise<void> {
  await apiClient.delete(`/safety/check-sheets/${sheetId}`);
}

export async function approveSafetyCheckSheet(sheetId: number): Promise<SafetyCheckSheet> {
  const { data } = await apiClient.post<SafetyCheckSheet>(
    `/safety/check-sheets/${sheetId}/approve`
  );
  return data;
}

export async function revokeSafetyCheckSheet(sheetId: number): Promise<SafetyCheckSheet> {
  const { data } = await apiClient.post<SafetyCheckSheet>(
    `/safety/check-sheets/${sheetId}/revoke`
  );
  return data;
}

export async function uploadSafetyCheckSheetAttachments(
  sheetId: number,
  files: File[]
): Promise<SafetyCheckSheetAttachment[]> {
  const form = new FormData();
  files.forEach((file) => form.append('files', file));
  const { data } = await apiClient.post<SafetyCheckSheetAttachment[]>(
    `/safety/check-sheets/${sheetId}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function deleteSafetyCheckSheetAttachment(
  sheetId: number,
  attachmentId: number
): Promise<void> {
  await apiClient.delete(`/safety/check-sheets/${sheetId}/attachments/${attachmentId}`);
}

export async function downloadSafetyCheckSheetAttachment(
  sheetId: number,
  attachmentId: number,
  fileName: string
): Promise<void> {
  const { data } = await apiClient.get<Blob>(
    `/safety/check-sheets/${sheetId}/attachments/${attachmentId}/download`,
    { responseType: 'blob' }
  );
  const url = window.URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(url);
}


export async function downloadSafetyPpeIssuancePdf(
  issuanceId: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/safety/ppe-issuances/${issuanceId}/pdf`,
    fileName || `보호구지급_${issuanceId}.pdf`
  );
}

export async function downloadSafetyCheckSheetPdf(
  sheetId: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/safety/check-sheets/${sheetId}/pdf`,
    fileName || `안전점검표_${sheetId}.pdf`
  );
}
